import { useCallback, useState } from 'react';

const STORAGE_KEY = 'wh-gobbo:dashboard-prefs';

export interface DashboardPrefs {
  /** The vault quick-access panel below the character boxes. Ships on. */
  showVaultPanel: boolean;
}

const DEFAULTS: DashboardPrefs = { showVaultPanel: true };

function read(): DashboardPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    // Spread over the defaults rather than replacing them, so a prefs object
    // written before a new key existed still gets that key's default.
    return raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<DashboardPrefs>) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

/**
 * Per-browser dashboard layout preferences. localStorage rather than the
 * server for the same reason useSystemHistory is: this is a view convenience,
 * not account data, and it does not justify an account_settings table.
 * Absent value means the default, so the panel works before anything is
 * written.
 */
export function useDashboardPrefs() {
  const [prefs, setPrefs] = useState<DashboardPrefs>(read);

  const set = useCallback(<K extends keyof DashboardPrefs>(key: K, value: DashboardPrefs[K]) => {
    setPrefs((prev) => {
      const next = { ...prev, [key]: value };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // Private browsing / quota — a layout preference is never worth
        // failing the render for.
      }
      return next;
    });
  }, []);

  return { prefs, set };
}
